import { Client, Product } from '../models';
import { connectionService, localNetworkService, apiService } from '../services';
import { printService } from '../print/PrintService';
import { Model, modelRegistry } from './Model';
import HashGen from '../utilities/HashGen';
import { Store } from '../wrappers';
export class AppEvent extends Model {
    static entity = 'app_events';
    static primaryKey = 'hash';
    static defaultCallback;
    static responseHandlers = new Map();
    hash = '';
    venue_hash = '';
    type = null;
    local = false;
    sender = '';
    recipient = '';
    request_hash = '';
    response_required = false;
    data = {};
    // Sender-side transport directive set by .preferLocalNetwork(). Deliberately NOT a
    // field() entry → it is never serialized by $toJson (the wire payload is unchanged)
    // and never set by $fill. null = the default dual-transport behavior of send().
    preferLanWindow = null;
    // Sender-side broadcast hook set by .onEachResponse(). Like preferLanWindow it is not
    // a field() entry, so it never reaches the wire or the store.
    responseListener = null;
    static fields() {
        return {
            hash: this.attr(HashGen.regular()),
            venue_hash: this.attr(''),
            type: this.attr(null),
            local: this.attr(false),
            sender: this.attr(''),
            recipient: this.attr(''),
            request_hash: this.attr(''),
            response_required: this.attr(false),
            data: this.attr({}),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    static build(type, data) {
        const tenant = Store.get('tenant');
        return new AppEvent({
            type,
            data,
            venue_hash: tenant?.venue?.hash || ''
        });
    }
    overLocalNetwork() {
        this.local = true;
        return this;
    }
    withResponse() {
        this.response_required = true;
        return this;
    }
    // Opt-in LAN-first send. When set, send() tries LAN first and escalates to WAN only
    // if no response arrives within windowMs (or immediately if LAN has no peers). It
    // takes effect only for a non-local, single-recipient .withResponse() send (it gates
    // on the one reply); otherwise send() behaves exactly as before. Not calling this at
    // all is a full no-op. windowMs should satisfy 0 < windowMs < the send() timeout
    // (a window >= timeout would escalate to WAN only as we give up — pointless).
    preferLocalNetwork(windowMs = 1000) {
        this.preferLanWindow = windowMs;
        return this;
    }
    // Opt-in for a .withResponse() broadcast (no recipient): the listener sees each reply
    // as it arrives. It only observes — send() still collects until its timeout and
    // resolves with every reply, exactly as without it. No effect on single-recipient sends.
    onEachResponse(listener) {
        this.responseListener = listener;
        return this;
    }
    async send(recipient, timeout = 2000) {
        const terminal = Store.get('terminal');
        if (terminal)
            this.sender = terminal.key;
        if (recipient)
            this.recipient = recipient;
        this.save();
        // LAN-first opt-in. Only diverges for a non-local, single-recipient .withResponse()
        // event: it gates WAN escalation on THE reply, so it needs a response, a specific
        // recipient to wait on, and a WAN leg to escalate to. A multi-recipient/broadcast
        // event (no `recipient`) has no single reply to gate on, so it falls through to the
        // normal dual-transport path. When .preferLocalNetwork() was not called,
        // preferLanWindow is null and the path below runs unchanged.
        if (this.preferLanWindow !== null && this.response_required && !this.local && recipient) {
            return this.sendLanFirst(recipient, timeout);
        }
        if (!this.local && connectionService.isOnline)
            apiService.post('/push-notifications', this.$toJson());
        const lanEventResponse = await this.sendOverLocalNetwork();
        if (this.local && lanEventResponse.status !== 'sent') {
            return [{ success: false, terminal: undefined }];
        }
        if (!this.response_required) {
            return [{ success: true, terminal: undefined }];
        }
        return new Promise((resolve, reject) => {
            let responses = [];
            // Setup a handler to resolve upon response.
            AppEvent.responseHandlers.set(this.hash, (response) => {
                responses.push(response);
                // Single-recipient request: the one reply IS the answer — settle now and
                // clear the handler. Broadcast (no recipient — e.g. detectPrinters): keep
                // the handler registered and accumulate every terminal's reply until the
                // timeout resolves with all of them; an .onEachResponse() listener sees each
                // one as it arrives. (handle() no longer deletes the handler, so a broadcast
                // collects more than just the first reply.)
                if (recipient) {
                    clearTimeout(timeoutId);
                    AppEvent.responseHandlers.delete(this.hash);
                    resolve(responses);
                }
                else {
                    // Contained: the listener only observes, so its error must not escape
                    // into the receive path (AppEvent.handle) that delivered this reply.
                    try {
                        this.responseListener?.(response);
                    }
                    catch (error) {
                        console.error(error);
                    }
                }
            });
            const timeoutId = setTimeout(() => {
                AppEvent.responseHandlers.delete(this.hash);
                if (responses.length > 0)
                    resolve(responses);
                else
                    resolve([{ success: false, error: 'timeout', terminal: undefined }]);
            }, timeout);
        });
    }
    /**
     * LAN-first send with WAN escalation — reached only when .preferLocalNetwork() was
     * set (and the event is .withResponse()). It tries LAN first and escalates to WAN
     * only if (a) LAN cannot send at all (no relay/peers → escalate immediately) or
     * (b) no response arrives within preferLanWindow ms.
     *
     * Resolution is asymmetric by design:
     *   • LAN reply within the window → resolve with the REAL reply (target is authoritative).
     *   • WAN escalation → resolve `success` immediately (fire-and-forget); the WAN round-trip
     *     is too slow to block the caller, and the 60s AppEvent backup poll re-delivers if the
     *     WAN copy was lost. (If we're offline so WAN can't fire, the overall timeout resolves
     *     a failure — an offline send to a non-LAN target genuinely cannot be delivered.)
     *
     * Receiver-side hash dedup makes the possible double-delivery (a slow LAN reply plus the
     * WAN copy) harmless — the event is still handled once. sender/recipient/save() are
     * already done by send().
     */
    sendLanFirst(recipient, timeout = 2000) {
        return new Promise((resolve) => {
            const responses = [];
            let settled = false;
            let wanFired = false;
            const fireWan = () => {
                if (wanFired || settled || this.local || !connectionService.isOnline)
                    return;
                wanFired = true;
                apiService.post('/push-notifications', this.$toJson());
                // Fire-and-forget over WAN. A cross-network round-trip (queued broadcast +
                // Soketi push, seconds on cellular) is too slow to block an operation like
                // issuing a receipt, so we resolve success at escalation rather than waiting
                // for the reply. Delivery is guaranteed by the 60s AppEvent backup poll, which
                // re-delivers this event if the WAN copy was lost. The target still replies;
                // that late reply simply finds no handler and is dropped. (The LAN path above
                // still returns the real reply when the target is reachable on the LAN.)
                settle([{ success: true, terminal: undefined }]);
            };
            const settle = (result) => {
                if (settled)
                    return;
                settled = true;
                clearTimeout(escalateId);
                clearTimeout(timeoutId);
                AppEvent.responseHandlers.delete(this.hash);
                resolve(result);
            };
            AppEvent.responseHandlers.set(this.hash, (response) => {
                if (!recipient) {
                    responses.push(response);
                    return;
                }
                settle([response]);
            });
            // Try LAN first; if it can't even send (no relay/peers — e.g. a terminal on a
            // different network), don't wait out the window — escalate to WAN immediately.
            // A rejected LAN bridge call (Electron IPC / native plugin) is treated the same.
            this.sendOverLocalNetwork()
                .then(lan => { if (lan.status !== 'sent')
                fireWan(); })
                .catch(() => fireWan());
            // Escalate to WAN if no response arrives within the LAN-first window. Clamped to
            // the overall timeout so WAN is always attempted before we give up, even if a
            // caller sets a window >= timeout.
            const escalateId = setTimeout(fireWan, Math.min(this.preferLanWindow ?? 1000, timeout));
            // Overall deadline — mirrors send()'s multi/single-recipient resolution.
            const timeoutId = setTimeout(() => {
                settle(responses.length > 0 ? responses : [{ success: false, error: 'timeout', terminal: undefined }]);
            }, timeout);
        });
    }
    async respond(data) {
        const terminal = Store.get('terminal');
        const tenant = Store.get('tenant');
        const appEvent = new AppEvent({
            type: this.type,
            data,
            local: this.local,
            response_required: false,
            request_hash: this.hash,
            sender: terminal?.key || '',
            recipient: this.sender,
            venue_hash: tenant?.venue?.hash || ''
        });
        appEvent.save();
        if (!this.local && connectionService.isOnline)
            apiService.post('/push-notifications', appEvent.$toJson());
        const lanEventResponse = await appEvent.sendOverLocalNetwork();
        return { success: !this.local || lanEventResponse.status === 'sent' };
    }
    async sendOverLocalNetwork() {
        if (!localNetworkService.connectionStatus?.connected || !localNetworkService.connectionStatus?.terminals.length) {
            return { status: 'failed', error: 'not_connected' };
        }
        return localNetworkService.send(this.$toJson());
    }
    static async handle(event) {
        console.log('Received Event', event.type);
        const tenant = Store.get('tenant');
        if (tenant?.venue?.hash !== event.venue_hash)
            return;
        // Dedup only on a real hash. A falsy hash must never be deduped: the
        // first null/empty-hash event would be stored under key `null`, and
        // every subsequent falsy-hash broadcast would then match find(null)
        // and be dropped for the rest of the session.
        if (event.hash && AppEvent.find(event.hash))
            return;
        const appEvent = new AppEvent(event);
        appEvent.save();
        const handler = this.responseHandlers.get(event.request_hash);
        if (handler) {
            // Do NOT delete the handler here. A single-recipient send deletes it itself
            // on first reply; a broadcast (e.g. detectPrinters) must keep it to collect
            // every terminal's reply until its timeout. Deleting here would drop all
            // replies after the first.
            handler(event.data);
            return;
        }
        // unhandled response
        if (event.request_hash)
            return;
        // Recipient routing for the WAN leg. On a LAN the Socket.IO relay
        // hand-delivers a recipient-addressed event to the single target socket,
        // so the receiver never had to check. Pusher fans every event out to the
        // whole tenant channel — so without this guard one remote print
        // would fire on every terminal. Broadcasts (no recipient — e.g. a
        // detectPrinters query or a syncTerminals push) are unaffected.
        const terminal = Store.get('terminal');
        if (event.recipient && event.recipient !== terminal?.key)
            return;
        // Storm guard. detectPrinters/print are the only handlers that send a reply, so
        // they are the only loop sources. Dispatch them ONLY for a genuine request
        // (response_required === true). A reply always carries response_required === false
        // (hardcoded in respond()), so even if its request_hash were stripped in transit —
        // letting it slip past the `if (event.request_hash) return` guard above and look
        // like a request — it still cannot be mistaken for one here, and is never re-answered.
        // Worst case degrades to a silent no-op, never the broadcast storm we hit before.
        // NOTE: scoped to these two only — syncTerminals/openDrawer/defaultCallback never
        // reply (response_required is false for them), so they must NOT be gated.
        if (event.response_required && event.type === 'detectPrinters') {
            await AppEvent.onDetectPrinters(appEvent);
        }
        if (event.response_required && event.type === 'print') {
            await AppEvent.onPrint(appEvent);
        }
        if (event.type === 'openDrawer') {
            await AppEvent.onOpenDrawer(appEvent);
        }
        if (event.type === 'syncTerminals') {
            await AppEvent.onSyncTerminals(appEvent);
        }
        if (AppEvent.defaultCallback) {
            await AppEvent.defaultCallback(appEvent);
        }
    }
    save() {
        const limit = 100;
        if (AppEvent.all().length >= limit) {
            AppEvent.create({ data: AppEvent.all().slice(limit / 2).map(ev => ev.$toJson()) });
        }
        this.$save();
    }
    static registerDefaultCallback(callback) {
        AppEvent.defaultCallback = callback;
    }
    static async onDetectPrinters(event) {
        const printers = await printService.detectPrinters(true);
        const terminal = Store.get('terminal');
        const terminalJson = terminal ? terminal.$toJson() : undefined;
        event.respond({ success: true, data: printers, terminal: terminalJson });
    }
    static async onPrint(event) {
        const { printer, data } = event.data;
        const printResult = await printService.sendToPrinter(printer, data, true);
        const terminal = Store.get('terminal');
        const terminalJson = terminal ? terminal.$toJson() : undefined;
        event.respond({ success: true, data: printResult, terminal: terminalJson });
    }
    static async onOpenDrawer(event) {
        printService.openDrawer(true);
    }
    static async onSyncTerminals(event) {
        const modelClass = Model.getModelClass(event.data.model);
        if (!modelClass)
            return;
        const deletedRecords = event.data.items.filter(i => i.action === 'delete').map(i => i.record);
        const updatedRecords = event.data.items.filter(i => i.action !== 'delete').map(i => i.record);
        if (updatedRecords.length) {
            if (modelClass === Product) {
                Product.mergeBroadcast(updatedRecords);
            }
            else {
                modelClass.insert({ data: updatedRecords });
            }
        }
        deletedRecords.forEach(r => {
            const record = modelClass.find(r[modelClass.primaryKey]);
            record?.$delete();
        });
        AppEvent.handleSyncTerminalsAdditional(event);
    }
    static async handleSyncTerminalsAdditional(event) {
        if (!event.data.additional)
            return;
        if (event.data.model === 'Order') {
            if (event.data.additional.client)
                Client.insert({ data: [event.data.additional.client] });
            if (event.data.additional.product_counts)
                Product.update(event.data.additional.product_counts);
        }
    }
}
export default AppEvent;
modelRegistry.register('AppEvent', AppEvent);
//# sourceMappingURL=AppEvent.js.map